import { TextField } from '@mui/material';
import { isAxiosError } from 'axios';
import { FormEvent, useEffect, useState } from 'react';

import {
    Actions,
    FieldGroup,
    Form,
    Modal,
    ModalHeader,
    ModalNotification,
    PrimaryButton,
    SecondaryButton,
    Section,
} from 'components/common/Modal';

import api from 'api';
import type { VisaDetails } from 'api/rest/application';
import useTranslate from 'hooks/useTranslate';

export interface ReceivedVisaApplication {
    id: number;
    candidate_id: number;
    program_name: string | null;
}

interface ApiErrorResponse {
    msg?: string;
}

interface ReceivedVisaModalProps {
    application: ReceivedVisaApplication | null;
    open: boolean;
    onClose: () => void;
    onVisaReceived: (visa: VisaDetails) => void | Promise<void>;
}

const ReceivedVisaModal = ({
    application,
    open,
    onClose,
    onVisaReceived,
}: ReceivedVisaModalProps) => {
    const { translate } = useTranslate();
    const [start, setStart] = useState('');
    const [end, setEnd] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setStart('');
            setEnd('');
            setHasSubmitted(false);
            setErrorMessage(null);
        }
    }, [open]);

    if (!application) {
        return null;
    }

    const hasStartDate = Boolean(start);
    const hasEndDate = Boolean(end);
    const datesAreOrdered = !hasStartDate || !hasEndDate || end >= start;
    const datesAreValid = hasStartDate && hasEndDate && datesAreOrdered;
    const showStartDateError = hasSubmitted && !hasStartDate;
    const showEndDateError = hasSubmitted && (!hasEndDate || !datesAreOrdered);

    const handleClose = () => {
        if (!isSaving) {
            onClose();
        }
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setHasSubmitted(true);

        if (!datesAreValid || isSaving) {
            return;
        }

        setIsSaving(true);
        setErrorMessage(null);

        try {
            const { data } = await api.application.recordReceivedVisa(application.candidate_id, {
                application_id: application.id,
                program_name: application.program_name || '',
                start,
                end,
            });

            await onVisaReceived({
                country: data.country,
                name: data.name,
                start: data.start,
                end: data.end,
            });
            onClose();
        } catch (error) {
            let message = translate('somethingWentWrong');

            if (isAxiosError<ApiErrorResponse>(error) && error.response?.data?.msg) {
                message = error.response.data.msg;
            }

            setErrorMessage(message);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal open={open} onClose={handleClose} size="sm" ariaLabel={translate('receivedVisa')}>
            <Form onSubmit={handleSubmit}>
                <ModalHeader
                    eyebrow={translate('visaResults')}
                    title={translate('receivedVisa')}
                    subtitle={translate('receivedVisaDescription')}
                />

                <Section>
                    <FieldGroup>
                        <TextField
                            fullWidth
                            label={translate('programOrService')}
                            value={application.program_name || ''}
                            slotProps={{
                                input: { readOnly: true },
                            }}
                        />
                    </FieldGroup>

                    <FieldGroup>
                        <TextField
                            autoFocus
                            fullWidth
                            required
                            type="date"
                            label={translate('startDate')}
                            value={start}
                            onChange={(event) => setStart(event.target.value)}
                            error={showStartDateError}
                            helperText={showStartDateError ? translate('dateRequired') : ' '}
                            InputLabelProps={{ shrink: true }}
                            disabled={isSaving}
                        />
                    </FieldGroup>

                    <FieldGroup>
                        <TextField
                            fullWidth
                            required
                            type="date"
                            label={translate('endDate')}
                            value={end}
                            onChange={(event) => setEnd(event.target.value)}
                            error={showEndDateError}
                            helperText={
                                showEndDateError
                                    ? !hasEndDate
                                        ? translate('dateRequired')
                                        : translate('endDateBeforeStartDate')
                                    : ' '
                            }
                            InputLabelProps={{ shrink: true }}
                            disabled={isSaving}
                        />
                    </FieldGroup>
                </Section>

                <Actions>
                    <SecondaryButton type="button" onClick={handleClose} disabled={isSaving}>
                        {translate('cancel')}
                    </SecondaryButton>
                    <PrimaryButton type="submit" disabled={!datesAreValid || isSaving}>
                        {isSaving ? translate('saving') : translate('saveVisaDetails')}
                    </PrimaryButton>
                </Actions>

                {errorMessage && (
                    <ModalNotification
                        variant="error"
                        title={translate('errorTitle')}
                        message={errorMessage}
                    />
                )}
            </Form>
        </Modal>
    );
};

export default ReceivedVisaModal;
